import { forwardRef } from 'react';
import FormInput from '../FormInput';
import FormTextarea from '../FormTextarea';
import type { ComponentPropsWithoutRef } from 'react';

// ============================================
// Text Types - Internal Types do Backend
// ============================================

// Text (base - unrestricted)
interface TextProps extends Omit<ComponentPropsWithoutRef<'textarea'>, 'rows'> {
    formInputSize?: 'sm' | 'lg';
}

export const Text = forwardRef<HTMLTextAreaElement, TextProps>(({ formInputSize, ...props }, ref) => {
    return <FormTextarea {...props} ref={ref} />;
});
Text.displayName = 'Text';

// Text.Paragraph (max 255, multiline)
interface TextParagraphProps extends Omit<ComponentPropsWithoutRef<'textarea'>, 'maxLength' | 'rows'> {
    formInputSize?: 'sm' | 'lg';
}

export const TextParagraph = forwardRef<HTMLTextAreaElement, TextParagraphProps>(({ formInputSize, ...props }, ref) => {
    return <FormTextarea {...props} ref={ref} maxLength={255} rows={4} />;
});
TextParagraph.displayName = 'TextParagraph';

// Text.Sentence (max 255, single line, allow spaces)
interface TextSentenceProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'maxLength'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const TextSentence = forwardRef<HTMLInputElement, TextSentenceProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="text" maxLength={255} formInputSize={formInputSize} rounded={rounded} />;
});
TextSentence.displayName = 'TextSentence';

// Text.Word (max 50, single line, no spaces)
interface TextWordProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'maxLength'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const TextWord = forwardRef<HTMLInputElement, TextWordProps>(({ formInputSize, rounded, onChange, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Remove spaces
        e.target.value = e.target.value.replace(/\s/g, '');
        onChange?.(e);
    };

    return <FormInput {...props} ref={ref} type="text" maxLength={50} onChange={handleChange} formInputSize={formInputSize} rounded={rounded} />;
});
TextWord.displayName = 'TextWord';

// Text.Name (max 100, allow spaces)
interface TextNameProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'maxLength'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const TextName = forwardRef<HTMLInputElement, TextNameProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="text" maxLength={100} formInputSize={formInputSize} rounded={rounded} />;
});
TextName.displayName = 'TextName';

// Text.Title (max 255, allow spaces)
interface TextTitleProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'maxLength'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const TextTitle = forwardRef<HTMLInputElement, TextTitleProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="text" maxLength={255} formInputSize={formInputSize} rounded={rounded} />;
});
TextTitle.displayName = 'TextTitle';

// Text.Email (max 254)
interface TextEmailProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'maxLength'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const TextEmail = forwardRef<HTMLInputElement, TextEmailProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="email" maxLength={254} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || 'email@example.com'} />;
});
TextEmail.displayName = 'TextEmail';

// Text.URL (max 2083)
interface TextURLProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'maxLength'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const TextURL = forwardRef<HTMLInputElement, TextURLProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="url" maxLength={2083} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || 'https://example.com'} />;
});
TextURL.displayName = 'TextURL';

// Text.Color
interface TextColorProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const TextColor = forwardRef<HTMLInputElement, TextColorProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="color" formInputSize={formInputSize} rounded={rounded} />;
});
TextColor.displayName = 'TextColor';

// Text.Slug (lowercase, hyphen-separated)
interface TextSlugProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'maxLength'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const TextSlug = forwardRef<HTMLInputElement, TextSlugProps>(({ formInputSize, rounded, onChange, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Convert to slug format: lowercase, hyphen-separated
        e.target.value = e.target.value
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');
        onChange?.(e);
    };

    return <FormInput {...props} ref={ref} type="text" maxLength={255} onChange={handleChange} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || 'my-slug-example'} />;
});
TextSlug.displayName = 'TextSlug';

// Text.Token (alphanumeric, uppercase)
interface TextTokenProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'maxLength'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const TextToken = forwardRef<HTMLInputElement, TextTokenProps>(({ formInputSize, rounded, onChange, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Alphanumeric uppercase only
        e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        onChange?.(e);
    };

    return <FormInput {...props} ref={ref} type="text" maxLength={255} onChange={handleChange} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || 'ABC123XYZ'} />;
});
TextToken.displayName = 'TextToken';

// Text.Mrz (Machine Readable Zone - passport MRZ reading)
interface TextMrzProps extends Omit<ComponentPropsWithoutRef<'textarea'>, 'rows' | 'maxLength'> {
    formInputSize?: 'sm' | 'lg';
}

export const TextMrz = forwardRef<HTMLTextAreaElement, TextMrzProps>(({ formInputSize, onChange, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        // MRZ format: uppercase, alphanumeric + < character
        e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9<\n]/g, '');
        onChange?.(e);
    };

    return (
        <FormTextarea
            {...props}
            ref={ref}
            rows={3}
            maxLength={300}
            onChange={handleChange}
            placeholder="P<BRASILEIRO<<NOME<COMPLETO<<<<<<<<<<<<<<<<<<\n1234567890BRA0001011M2912314<<<<<<<<<<<<<<04"
            style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
        />
    );
});
TextMrz.displayName = 'TextMrz';
